import { isFutureDate } from '@/lib/formatter-utils';
import { formatDateTimeInputValueForTimeZone, startOfTomorrowInputValue } from '@/lib/time-zone';
import type { BlogPublishIntentMode, Page, PageContentModel } from '@/types/page';
import { PageType } from '@/types/page';

const LOCAL_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/;
// Scheduled publishing is polled every 5 minutes; keep the UI from showing "missed"
// during the normal wait for the next scheduler run and job execution.
const SCHEDULE_STATUS_GRACE_MS = 10 * 60 * 1000;

function scheduleTime(value?: string | number | Date | null): number | null {
  if (value === null || value === undefined || value === '') return null;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.getTime();
}

export function isScheduleMissed(
  value?: string | number | Date | null,
  graceMs = SCHEDULE_STATUS_GRACE_MS,
): boolean {
  const time = scheduleTime(value);
  return time !== null && time + graceMs <= Date.now();
}

export function isSchedulePendingOrInGrace(value?: string | number | Date | null): boolean {
  const time = scheduleTime(value);
  return time !== null && !isScheduleMissed(value);
}

export function earliestSchedulableAt(timeZone?: string | null) {
  return startOfTomorrowInputValue(new Date(), timeZone);
}

export function currentDateTimeInputValue(timeZone?: string | null) {
  return formatDateTimeInputValueForTimeZone(new Date(), timeZone);
}

export function toDateTimeValue(value: string) {
  if (!value) return value;
  if (value.includes('T')) return value;
  return `${value}T00:00`;
}

export function toPublishAtInputValue(
  value?: string | null,
  timeZone?: string | null,
): string | null {
  if (!value) return null;
  if (LOCAL_DATE_TIME_PATTERN.test(value)) return value.slice(0, 16);
  if (value.includes('T')) return formatDateTimeInputValueForTimeZone(value, timeZone) || null;
  return `${value}T00:00`;
}

type ScheduleEligibilityPage =
  | Pick<Page, 'isPublished' | 'pageType' | 'scheduledPublishAt'>
  | null
  | undefined;
export type PublishAtInputSource = 'editor' | 'sectionPublished' | 'published' | 'scheduled' | null;

export interface PublishAtInput {
  source: PublishAtInputSource;
  value?: string | null;
}

export function readEditorPublishAt(contentModel: PageContentModel | null | undefined) {
  if (!contentModel) return undefined;

  if ('editorPublishAt' in contentModel) {
    return contentModel.editorPublishAt ?? null;
  }

  return undefined;
}

function readPublishedAt(contentModel: PageContentModel | null | undefined) {
  if (!contentModel || contentModel.publishedAt == null) return undefined;

  return contentModel.publishedAt;
}

function draftContentModelFromSection(page: Page | null | undefined): PageContentModel | null {
  if (!page) return null;

  const sectionContentModel = page.slots?.main.sections.find(
    (section) =>
      section.sectionType === page.pageType || section.sectionTemplateCode === page.pageType,
  )?.props?.contentModel;

  if (
    !sectionContentModel ||
    typeof sectionContentModel !== 'object' ||
    Array.isArray(sectionContentModel)
  ) {
    return null;
  }

  return sectionContentModel as PageContentModel;
}

export function resolvePublishAtInput(
  page: Page | null | undefined,
  contentModel: PageContentModel | null | undefined = page?.contentModel,
): PublishAtInput {
  // Section editor values include pending previews and take precedence over saved page values.
  const draftContentModel = draftContentModelFromSection(page);
  const sectionEditorPublishAt = readEditorPublishAt(draftContentModel);
  if (sectionEditorPublishAt !== undefined) {
    return { source: 'editor', value: sectionEditorPublishAt };
  }

  const hasActiveSchedule =
    page?.scheduledPublishAt != null && isSchedulePendingOrInGrace(page.scheduledPublishAt);
  const editorPublishAt = readEditorPublishAt(contentModel);
  if (editorPublishAt !== undefined) {
    return { source: 'editor', value: editorPublishAt };
  }

  const pageEditorPublishAt =
    contentModel === page?.contentModel ? undefined : readEditorPublishAt(page?.contentModel);
  if (pageEditorPublishAt !== undefined) {
    return { source: 'editor', value: pageEditorPublishAt };
  }

  if (hasActiveSchedule) {
    return { source: 'scheduled', value: page.scheduledPublishAt };
  }

  // Draft date fields and committed dates are stored as instants, then formatted for the input.
  const sectionPublishedAt = readPublishedAt(draftContentModel);
  if (sectionPublishedAt !== undefined) {
    return { source: 'sectionPublished', value: sectionPublishedAt };
  }
  const publishedAt = readPublishedAt(contentModel);
  if (publishedAt !== undefined) {
    return { source: 'published', value: publishedAt };
  }
  if (page?.scheduledPublishAt != null) {
    return { source: 'scheduled', value: page.scheduledPublishAt };
  }

  return { source: null, value: undefined };
}

function scheduleHasChanged({
  isScheduled,
  publishAtInput,
  scheduledDateInAccountTimeZone,
}: {
  isScheduled: boolean;
  publishAtInput: string | null;
  scheduledDateInAccountTimeZone: string | null;
}) {
  if (!isScheduled || !scheduledDateInAccountTimeZone) return true;

  // Match commit semantics: schedule publish parses the editor value in the current account zone.
  return publishAtInput !== scheduledDateInAccountTimeZone;
}

export function canScheduleAt(
  page: ScheduleEligibilityPage,
  publishAt: string | null,
  earliestScheduleAt: string,
  currentDateTimeAt = '',
  scheduledDateTimeAt = '',
) {
  const currentDate = currentDateTimeAt.split('T')[0];
  const publishDate = publishAt?.split('T')[0];
  const scheduledDate = scheduledDateTimeAt.split('T')[0];
  const hasNewScheduleDate = Boolean(
    publishAt && earliestScheduleAt && publishAt >= earliestScheduleAt,
  );
  const hasActiveScheduleUpdate = Boolean(
    publishAt &&
      publishDate &&
      currentDate &&
      scheduledDate &&
      publishDate === currentDate &&
      scheduledDate === currentDate &&
      currentDateTimeAt &&
      page?.scheduledPublishAt &&
      isFutureDate(page.scheduledPublishAt) &&
      publishAt > currentDateTimeAt,
  );
  return Boolean(
    page?.pageType === PageType.BLOG &&
      !page?.isPublished &&
      (hasNewScheduleDate || hasActiveScheduleUpdate),
  );
}

export function deriveBlogPublishIntentMode(
  page: ScheduleEligibilityPage,
  publishAt: string | null,
  earliestScheduleAt: string,
  currentDateTimeAt = '',
  scheduledDateTimeAt = '',
): BlogPublishIntentMode {
  return canScheduleAt(page, publishAt, earliestScheduleAt, currentDateTimeAt, scheduledDateTimeAt)
    ? 'scheduled'
    : 'immediate';
}

export interface PageScheduleState {
  publishAtInput: string | null;
  publishIntentMode: BlogPublishIntentMode;
  canSchedule: boolean;
  isScheduled: boolean;
  isScheduleMissed: boolean;
  hasScheduleDateChanged: boolean;
  scheduledDateInAccountTimeZone: string | null;
}

export function pageScheduleState(
  page: Page | null | undefined,
  timeZone?: string | null,
): PageScheduleState {
  const earliestScheduleAt = earliestSchedulableAt(timeZone);
  const currentDateTimeAt = currentDateTimeInputValue(timeZone);
  const scheduledDateInAccountTimeZone = page?.scheduledPublishAt
    ? formatDateTimeInputValueForTimeZone(page.scheduledPublishAt, timeZone) || null
    : null;
  const resolvedPublishAtInput = resolvePublishAtInput(page);
  // The input value may fall back to scheduledPublishAt, but scheduledPublishAt remains the persisted source.
  const publishAtInput = toPublishAtInputValue(resolvedPublishAtInput.value, timeZone);
  const canSchedule = canScheduleAt(
    page,
    publishAtInput,
    earliestScheduleAt,
    currentDateTimeAt,
    scheduledDateInAccountTimeZone ?? '',
  );
  const publishIntentMode = deriveBlogPublishIntentMode(
    page,
    publishAtInput,
    earliestScheduleAt,
    currentDateTimeAt,
    scheduledDateInAccountTimeZone ?? '',
  );
  const schedulePending = isSchedulePendingOrInGrace(page?.scheduledPublishAt);
  const scheduleMissed = isScheduleMissed(page?.scheduledPublishAt);
  const hasScheduleDateChanged = scheduleHasChanged({
    isScheduled: schedulePending,
    publishAtInput,
    scheduledDateInAccountTimeZone,
  });

  return {
    publishAtInput,
    publishIntentMode,
    canSchedule,
    isScheduled: schedulePending,
    isScheduleMissed: scheduleMissed,
    hasScheduleDateChanged,
    scheduledDateInAccountTimeZone,
  };
}
